// Libs
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// Components
import Badge from '@material-ui/core/Badge';
import Popover from '@material-ui/core/Popover';
import classNames from 'classnames';
import { Tooltip } from '@material-ui/core';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import dayjs from 'dayjs';
import { useHistory } from 'react-router';
import moment from 'moment';
import { capitalize, groupBy } from 'lodash';
// Context
import { useComponentsPool } from '../../../ComponentsPool';
// Actions & Helpers
import { updateNotification, deleteNotification, deleteNotifications } from '../actions';
import {
  getAppLanguage,
  getInputFileUrlForThumbnail,
  getTimeSuffixFormat, iconForFileWithoutThumbnail
} from '../../layout/helper';
import Icon from '../../layout/components/Icon';
import { getKanbanVersionWsValue } from '../../core/helper';
import { END_DATE_PARAM, PARAMS_DATE_FORMAT, START_DATE_PARAM } from '../../rounds/constants';


const NotificationMenu = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { Component } = useComponentsPool();
  const [t] = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [id, setId] = useState(undefined);
  const users = useSelector(state => state.users.list);
  const currentUser = useSelector(state => state.users.currentUser);
  const workspaceSettings = useSelector(state => state.workspace.settings);
  const notifications = useSelector(state => state.navbar.notificationsList);
  const kanbanVersion = getKanbanVersionWsValue(workspaceSettings?.kanban_version, currentUser);
  const [groupedNotifs, setGroupedNotifs] = useState([]);
  const [notSeenNotifs, setNotSeenNotifs] = useState(false);
  const [currentTab, setCurrentTab] = useState(0)

  // Get the right date suffix for the notifs depending on the app language
  moment.locale(getAppLanguage(), getTimeSuffixFormat());

  useEffect(() => {
    setNotSeenNotifs(notifications.flat().filter(notif => !notif.seen_at));
    const filterNotifs = currentTab === 0 ? notifications.filter(groupedNotif => groupedNotif.some(notif => !notif.seen_at)) : notifications
    filterNotifs.forEach(groupedNotification => { // Sort the grouped notifications so the newest appears first
      if (groupedNotification.length > 0) {
        groupedNotification.sort((a, b) => (a?.created_at < b?.created_at ? 1 : -1));
      }
    });
    // Sort the notifications so the newest appears first
    setGroupedNotifs(filterNotifs.sort((a, b) => (a[0]?.created_at < b[0]?.created_at ? 1 : -1)));
  }, [notifications, currentTab]);

  useEffect(() => {
    setId(open ? 'notification-popover' : undefined);
  }, [open]);

  // On notification delete handler
  const handleClickDelete = groupedNotif => {
    // Delete only the required notifs or every notifs
    const notifsToDelete = groupedNotif.flat().map(notif => notif.id)

    // Delete the notifs
    notifsToDelete.forEach(notifToDelete => dispatch(deleteNotification(notifToDelete)));
  };

  // On notification delete all handler
  const handleClickDeleteAll = () => {
    // Delete every notifs
    dispatch(deleteNotifications());
  };

  // On notification click mark as read handler
  const handleClickMarkAsRead = (groupedNotif = null, redirectTo = null) => {
    // Mark only the required notifs as read or every notifs
    const notifsToUpdate = groupedNotif
      ? groupedNotif.flat().filter(notif => !notif.seen_at).map(notif => notif.id)
      : groupedNotifs.flat().filter(notif => !notif.seen_at).map(notif => notif.id);

    // Update the notifs
    notifsToUpdate.forEach(notif => {
      dispatch(updateNotification(notif, { seen_at: moment().format() }));
    });

    if (redirectTo) {
      history.push(redirectTo);
      setAnchorEl(null);
    }
  };

  const buildRedirectionUrl = (notification) => {
    const modelName = notification.model_name;
    if (modelName === 'Ticket' || modelName === 'TicketComment') {
      const url = `/tickets?key=${notification.ticketKey}`;
      return kanbanVersion === 'NEW' ? `${url}&ticketId=${notification.ticketId}` : url;
    }
    if (modelName.startsWith('JOBS')) {
      const searchParams = new URLSearchParams();
      searchParams.set('job', notification.object._id);
      searchParams.set('_refresh', Date.now());
      return `/rounds?${searchParams}`;
    }
  };

  const getNotifSentence = (groupedNotif, notifType, isExternalNotif) => {
    const [{ action_type: actionType, sender_name: senderName, ticketKey, object }] = groupedNotif;
    const nbSenders = Object.keys(groupBy(groupedNotif, 'sender_name')).length;
    const nbNotifs = groupedNotif.length;

    let avatar, author, subject, verb;

    switch (actionType) {
      case 'CREATE': // Ticket comment notifs
        if (isExternalNotif && nbSenders === 1) {
          avatar = notifType;
        } else {
          avatar = nbNotifs === 1 ? 'faComment' : 'faComments';
        }
        subject = `${t('tickets:the_ticket')} ${ticketKey}`;
        if (nbSenders === 1) {
          author = senderName;
          verb = isExternalNotif
            ? t('messaging:commented_messaging', { messaging_type: capitalize(notifType.split('_MESSAGE')[0]) })
            : t('tickets:commented');
        } else {
          author = t('common:x_persons', { nbPersons: nbSenders });
          verb = t('tickets:commented_plural');
        }
        break;
      case 'CREATE_TICKET':
        avatar = 'ticketsFilled';
        author = senderName;
        verb = t('tickets:created_ticket');
        subject = `${t('tickets:a_new_ticket')} ${ticketKey}`;
        break;
      case 'UPDATE_STATUS':
        avatar = 'faArrowsRotate';
        author = senderName;
        verb = t('tickets:updated_status');
        subject = `${t('tickets:of_ticket')} ${ticketKey}`;
        break;
      case 'UPDATE_TICKET':
        avatar = 'faArrowsRotate';
        author = senderName;
        verb = t('tickets:modified');
        subject = `${t('tickets:the_ticket')} ${ticketKey}`;
        break;
      case 'ADD_ASSIGNEES':
        avatar = 'userPlusFilled';
        author = senderName;
        verb = t('tickets:assigned_you');
        subject = `${t('tickets:to_ticket')} ${ticketKey}`;
        break;
      case 'ADD_FOLLOWERS':
        avatar = 'userPlusFilled';
        author = senderName;
        verb = t('tickets:subscribed_you');
        subject = `${t('tickets:to_ticket')} ${ticketKey}`;
        break;
      case 'CREATE_NOT_COMPLETED_JOBS':
        avatar = 'JOBS_not_completed';
        author = object.name;
        verb = t('rounds:not_completed_with_rate', { notCompletedTasksCount: object.notCompletedTasksCount, totalTasksCount: object.totalTasksCount });
        break;
    }
    return {
      avatar, author, subject, verb
    };
  };

  const getNotificationDate = notifCreationDate => {
    const dateNow = new Date();
    const date = new Date(notifCreationDate);
    const isToday = date.getDate() === dateNow.getDate() && date.getMonth() === dateNow.getMonth() && date.getFullYear() === dateNow.getFullYear();
    const differenceInMinutes = Math.round((dateNow - date) / (1000 * 60));
    const notifDateSentence = ` • ${dayjs(notifCreationDate).format('DD/MM/YY')}`;

    if (differenceInMinutes < 1) { // Lower than 1 min
      return t('common:just_now');
    }
    if (differenceInMinutes < 10) { // Lower than 10 min
      return t('common:x_minutes_ago', { nbMinutes: differenceInMinutes }) + notifDateSentence;
    }
    if (differenceInMinutes < 60) { // Lower than 1 hour, sentence should be : 5min ago, 10min ago, ....
      return t('common:x_minutes_ago', { nbMinutes: Math.floor(differenceInMinutes / 5) * 5 }) + notifDateSentence;
    }
    if (isToday) { // Today
      const hourAndMinutes = `${date.getHours()}h${date.getMinutes()}`;
      return t('common:today_at_x', { hourAndMinutes }) + notifDateSentence;
    }
    if (differenceInMinutes < 2880) { // Lower than 48 hours
      return `${t('common:the')} ${dayjs(notifCreationDate).format('DD/MM/YY')}`;
    }
    // More than 48 hours
    const days = Math.floor(differenceInMinutes / 1440); // 1440 minutes in a day
    if (days === 1) {
      return t('common:x_day_ago');
    }
    return t('common:x_days_ago', { nbDays: days });
  };

  const getNotificationContent = groupedNotif => {
    // Those variables changes depending on the number of senders or grouped notifications
    let content;
    // No matter the number of senders or grouped notifications, this variable doesn't change
    const creation_date = groupedNotif[0]?.created_at;

    if (groupedNotif.length === 1) { // ONE NOTIF
      if (groupedNotif[0].object?.urls?.length > 0) {
        let displayedFiles = groupedNotif[0].object.urls;
        const notifTextContent = groupedNotif[0].object.content;
        const notifWithTextAndDoc = displayedFiles.length > 0 && notifTextContent;
        let hiddenFiles = [];
        if (displayedFiles.length > 3) {
          hiddenFiles = displayedFiles.slice(3);
          displayedFiles = displayedFiles.slice(0, 3);
        }
        content = (
          <>
            {notifTextContent && <p className={classNames([{ 'notif-with-text-and-doc': notifWithTextAndDoc }])}>{notifTextContent}</p>}
            <div className={'notif-files'}>
              {displayedFiles.map(url => {
                const fileType = url?.split('.')?.pop();
                const fileThumbnailUrl = getInputFileUrlForThumbnail(url, fileType);
                return (
                  fileThumbnailUrl
                    ? <img src={fileThumbnailUrl} alt={fileType} />
                    : (
                      <div className={'notif-file-without-preview'}>
                        <Icon
                          componentName={'Icon'}
                          type={iconForFileWithoutThumbnail(fileType)}
                        />
                      </div>
                    )
                );
              })}
              {hiddenFiles.length > 0 && (
                <div className={'hidden-notif-files'}>
                  {getInputFileUrlForThumbnail(hiddenFiles[0], hiddenFiles[0].split('.').pop()) && (
                    <img
                      src={getInputFileUrlForThumbnail(hiddenFiles[0], hiddenFiles[0].split('.').pop())}
                      alt={hiddenFiles[0].split('.').pop()}
                    />
                  )}
                  <p>{`+${hiddenFiles.length}`}</p>
                </div>
              )}
            </div>
          </>
        );
      } else {
        content = <p>{groupedNotif[0].object?.content}</p>;
      }
    } else { // MULTIPLE NOTIFS
      content = t('tickets:new_comments', { nbComments: groupedNotif.length });
    }
    const notifType = groupedNotif[0].object?.custom_field?.type;
    const sender = users.find(user => user?.id === groupedNotif[0]?.sender?.id);
    if (sender && sender.username === 'qrcodes@merciyanis.com') {
      groupedNotif[0].sender_name = 'Un capteur';
    }
    const isExternalNotif = ['DOIT_MESSAGE', 'TEAMS_MESSAGE'].includes(notifType);
    const notifSentence = getNotifSentence(groupedNotif, notifType, isExternalNotif);
    return (
      <>
        <div className={'notification-avatar-and-text'}>
          <div className={classNames(['notification-avatar', { 'smaller-size-icon': notifSentence.avatar === 'faComment' }, { 'custom-size-icon': isExternalNotif }])}>
            <Icon type={notifSentence.avatar} />
          </div>
          <div className="form-text">
            <div className="title">
              <div className="author">{notifSentence.author}</div>
              <div className="verb">{notifSentence.verb}</div>
              <div className="subject">{notifSentence.subject}</div>
            </div>
            <div className={'notification-date'}>{getNotificationDate(creation_date)}</div>
            {groupedNotif[0].action_type === 'CREATE' && <div className="explanation">{content}</div>}
            {groupedNotif.flat().some(notif => !notif.seen_at)
              && (
                <div
                  className={'notification-read-button'}
                  role={'button'}
                  onClick={e => {
                    handleClickMarkAsRead(groupedNotif);
                    e.stopPropagation();
                  }}
                >
                  {t('common:read')}
                </div>
              )
            }
          </div>
          <div className={'notification-trash-wrapper'}>
            <div
              className={'notification-trash'}
              role={'button'}
              onClick={e => {
                handleClickDelete(groupedNotif);
                e.stopPropagation();
              }}
            >
              <Icon type={'trash'} />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Tooltip
        className={'tooltip_edit_alert'}
        classes={{ tooltip: 'table-tooltip' }}
        placement={'bottom'}
        title={t('common:notifications')}
      >
        <div
          aria-describedby={id}
          role={'button'}
          tabIndex={0}
          className={'notification-icon'}
          onClick={e => setAnchorEl(e.currentTarget)}
        >
          <Badge color={'primary'} badgeContent={0} showZero={notSeenNotifs.length > 0} variant="dot">
            <Component componentName={'Icon'} type={anchorEl ? 'bellTicketFilled' : 'bellTicket'} />
          </Badge>
        </div>
      </Tooltip>
      <Popover
        id={id}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className={'notifications-popover-wrapper'}>
          <div className={'notifications-popover-tabs'}>
            <Tabs
              value={currentTab}
              onChange={(e, tab) => setCurrentTab(tab)}
              aria-label={'notification-tabs'}
            >
              <Tab
                icon={<div className={notSeenNotifs.length === 0 ? '' : 'notification-tab-icon'}>{notSeenNotifs.length === 0 ? '✅' : notSeenNotifs.length}</div>}
                iconPosition="end"
                disableRipple
                label={t('common:not_read')}
              />
              <div className={'vertical-line'} />
              <Tab disableRipple label={t('common:see_all')} />
            </Tabs>
          </div>
          <div className={'notifications-main-buttons'}>
            <div className={'notifications-main-button'} role={'button'} onClick={() => handleClickMarkAsRead()}>{t('common:mark_everything_as_read')}</div>
            <div className={'notifications-main-button'} role={'button'} onClick={() => handleClickDeleteAll()}>{t('common:delete_all')}</div>
          </div>
          <div className={'notifications-popover-content'}>
            {groupedNotifs.map(groupedNotif => (
              <div
                key={groupedNotif.id}
                className={classNames([
                  'notification-wrapper',
                  { 'notification-is-not-seen': groupedNotif.length > 1 ? groupedNotif.find(notif => !notif.seen_at) : !groupedNotif[0].seen_at }
                ])}
                role={'button'}
                tabIndex={0}
                onClick={() => handleClickMarkAsRead(groupedNotif, buildRedirectionUrl(groupedNotif[0]))}
              >
                {getNotificationContent(groupedNotif)}
              </div>
            ))}
            <div className={'notification-wrapper no-more-notification-wrapper'}>
              <Component componentName={'Icon'} type={'teaCup'} />
              <p>{t(`common:${groupedNotifs.length !== 0 ? 'no_other_notif_the_last_thirty_days' : 'no_notif_the_last_thirty_days'}`)}</p>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default NotificationMenu;
